import { Fragment } from 'react';
import {
  Box,
  ContentPanel,
  Grid,
  Link,
  ProfileImage,
  ProfileInitials,
  SimpleLabel,
  Skeleton,
  Text,
} from '@hopskipdrive/react-components';
import { colorPalette, colorTokens } from '@rideiq/shared/components/variables';
import { includes, unionBy } from 'lodash';

import { RIDE_STATES } from '../../../constants';
import { formatDate } from '../../../utils/helpers';
import { useRideEditor } from '../../hooks/useRideEditor';

interface RideDetailsHeadingProps {
  isReadOnly?: boolean;
}

export const RideDetailsHeading: React.FC<RideDetailsHeadingProps> = ({ isReadOnly = false }) => {
  const { getRiderOptions, rideDetails } = useRideEditor();
  const { newRemovedRiders } = getRiderOptions();

  if (!rideDetails) {
    return null;
  }

  const getStatusLabelProps = () => {
    switch (true) {
      case includes(RIDE_STATES.upcoming, rideDetails?.status):
        return { bgColor: 'light-blue', label: 'Upcoming' };
      case includes(RIDE_STATES.inProgress, rideDetails?.status):
        return { bgColor: 'light-green', label: 'In Progress' };
      case includes(RIDE_STATES.completed, rideDetails?.status):
        return { bgColor: 'light-purple', label: 'Completed' };
      case includes(RIDE_STATES.canceled, rideDetails?.status):
        return { bgColor: 'light-orange', label: 'Canceled' };
      case includes(RIDE_STATES.requested, rideDetails?.status):
        return { bgColor: 'neutralVariant90', label: 'Requested' };
      default:
        return { bgColor: '', label: '' };
    }
  };

  const riders = rideDetails?.waypoints?.flatMap((waypoint) => {
    return waypoint.riders;
  });

  const removedRiderSlugs = newRemovedRiders?.map((rider) => rider.slug);
  const removedRiders = unionBy(newRemovedRiders, rideDetails?.removedPassengers, 'slug');

  return (
    <ContentPanel mb={2.5} style={{ padding: 30 }} data-testid="details-heading">
      <Grid container>
        <Grid item xs={12} md={12} lg={12}>
          <Box display="flex" justifyContent="space-between" mb={3}>
            <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center">
              <Text variant="h2" fontWeight={500} mr={2.5}>
                {/* Format ex: Monday, Dec 13 */}
                {rideDetails?.startsAt && formatDate(rideDetails?.startsAt, 'EEEE, LLL d')} Ride
              </Text>
            </Box>
            <SimpleLabel
              mr={1}
              textColor="text"
              style={{ padding: '4px 16px' }}
              data-testid="ride-status"
              {...getStatusLabelProps()}
            />
          </Box>
          <Box display="flex" alignItems="center">
            <Box display="flex" mr={2.5}>
              {!riders.length ? (
                <Skeleton variant="circular" width={40} height={40} animation="wave" />
              ) : (
                <Box display="flex" alignItems="center">
                  {riders?.map((rider) =>
                    rider.profilePhoto && rider.profilePhoto.indexOf('default_photo') === -1 ? (
                      <ProfileImage key={rider.fullName} imgSrc={rider.profilePhoto} />
                    ) : (
                      <ProfileInitials key={rider.fullName} size={40} fullName={rider.fullName} />
                    )
                  )}
                  {rideDetails?.removedPassengers.map((removedRider) => (
                    <ProfileInitials key={removedRider.fullName} size={40} fullName={removedRider.fullName} />
                  ))}
                </Box>
              )}
            </Box>
            <Box display="flex" flexWrap="wrap">
              {!riders.length ? (
                <Skeleton variant="text" width={100} />
              ) : (
                <Fragment>
                  {riders.map(
                    (rider, riderIndex, { length }) =>
                      removedRiderSlugs?.indexOf(rider.slug) === -1 && (
                        <Fragment key={rider.fullName}>
                          {isReadOnly ? (
                            rider.fullName
                          ) : (
                            <Link
                              ml={riderIndex > 0 ? 1 : 0}
                              href={`/riders/${rider.slug}`}
                              target="_blank"
                              data-testid="link-to-rider-profile"
                              aria-label={`Rider on ride ${rider.fullName}`}
                            >
                              {`${rider.fullName}`}
                            </Link>
                          )}
                          {(riderIndex + 1 < length ||
                            (riderIndex + 1 == length && removedRiders && removedRiders.length > 0)) &&
                            ', '}
                        </Fragment>
                      )
                  )}
                  {removedRiders &&
                    removedRiders.map((removedRider, removedRiderIndex, { length }) => (
                      <Fragment key={removedRider.fullName}>
                        <Link
                          ml={removedRiderIndex + 1 <= length ? 1 : 0}
                          style={{
                            color: colorTokens.text.medium,
                          }}
                          href={`/riders/${removedRider.slug}`}
                          target="_blank"
                          data-testid="link-to-rider-profile"
                          aria-label="Rider removed from ride"
                        >
                          {removedRider.fullName}
                        </Link>
                        <span style={{ color: colorPalette.red55, paddingLeft: '4px' }}>
                          (
                          {removedRider.riderStatus === 'not_riding'
                            ? 'not riding'
                            : removedRider.riderStatus.replace('_', '-')}
                          )
                        </span>
                        {removedRiderIndex + 1 < length && ', '}
                      </Fragment>
                    ))}
                </Fragment>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ContentPanel>
  );
};
